import type { CoverageFact } from './CoverageParts';
import type { CoverageLine } from './coverage.types';
import { daysUntil } from './coverageDates';
import { ABSENT, date, money, relativeDays } from './coverageFormat';

/**
 * The label / value / note triples the tile and the rows both show, built
 * once. Policies and quotes deliberately differ in wording -- "Annual premium"
 * against "Indicated premium", "Aggregate limit" against "Proposed aggregate
 * limit" -- because a quote is not coverage and should never read as though it
 * were.
 */

const endorsementNote = (count: number | undefined) =>
  count ? 'In effect this term' : 'None this term';

export function heroFacts(
  line: CoverageLine,
  now: string | number | Date = Date.now()
): CoverageFact[] {
  // No note. The payload carries `deductible` and nothing that says whether it
  // applies per claim or per policy period, and the tile should not assert a
  // basis it cannot support. Restore a note here only once the read carries one.
  const retention: CoverageFact = {
    label: 'Retention',
    value: money(line.retention),
  };

  if (line.kind === 'quote') {
    const days = daysUntil(line.expiresOn, now);

    return [
      retention,
      {
        label: 'Indicated premium',
        value: money(line.premium),
        note: 'Locked until expiry',
      },
      {
        label: 'Valid through',
        value: date(line.expiresOn),
        note:
          days === undefined ? 'No expiry on file' : `${relativeDays(days)}`,
      },
    ];
  }

  const facts: CoverageFact[] = [
    retention,
    {
      label: 'Annual premium',
      value: money(line.premium),
      note: 'For the full term',
    },
  ];

  // Omitted rather than shown as an em dash. /latest returns the policy entity,
  // which carries no endorsement count at all, so the tile printed "—" beside a
  // note reading "None this term" -- absence and zero asserted in one breath.
  if (line.endorsementCount !== undefined) {
    facts.push({
      label: 'Endorsements',
      value: String(line.endorsementCount),
      note: endorsementNote(line.endorsementCount),
    });
  }

  return facts;
}

export function rowFacts(
  line: CoverageLine,
  now: string | number | Date = Date.now()
): CoverageFact[] {
  const isQuote = line.kind === 'quote';
  const days = daysUntil(isQuote ? line.expiresOn : line.termEnd, now);

  return [
    {
      label: isQuote ? 'Proposed limit' : 'Limit',
      value: money(line.aggregateLimit),
    },
    { label: 'Retention', value: money(line.retention) },
    {
      label: isQuote ? 'Premium' : 'Annual premium',
      value: money(line.premium),
    },
    isQuote ? expiryFact(line, days) : termFact(line),
  ];
}

/** Split out because a ternary inside a ternary is forbidden by the repo's house rule. */
function expiryFact(
  line: CoverageLine,
  days: number | undefined
): CoverageFact {
  if (days === undefined) {
    return { label: 'Expires', value: date(line.expiresOn) };
  }

  return {
    label: 'Expires',
    value: `${date(line.expiresOn)} · ${relativeDays(days)}`,
  };
}

const termFact = (line: CoverageLine): CoverageFact => ({
  label: 'Term',
  value: `${date(line.effectiveDate)} — ${date(line.termEnd)}`,
});

export interface PremiumDelta {
  readonly amount: number;
  readonly direction: 'more' | 'less';
  /** Ready-to-read: "$760 more than your current premium". */
  readonly phrase: string;
}

/**
 * The renewal band's premium comparison.
 *
 * The prototype hardcoded the fixture's current premium into the subtraction,
 * so the delta was nonsense on every other account, and phrased the result as
 * "more" regardless of sign -- a cheaper renewal read "-$760 more than your
 * current premium".
 */
export function premiumDelta(
  current: CoverageLine | undefined,
  renewal: CoverageLine | undefined
): PremiumDelta | undefined {
  const from = current?.premium;
  const to = renewal?.premium;

  if (from === undefined || to === undefined || from === to) {
    return undefined;
  }

  const difference = to - from;
  const direction = difference > 0 ? 'more' : 'less';

  return {
    amount: Math.abs(difference),
    direction,
    phrase: `${money(
      Math.abs(difference)
    )} ${direction} than your current premium`,
  };
}
